import { useState, useMemo } from "react";
import { PencilSimple, Check, X, CaretDown, CaretRight, WarningCircle, CheckCircle, Info } from "@phosphor-icons/react";

// ── Helpers ──
const fmt$ = (v) => {
  const n = Number(v);
  if (isNaN(n)) return String(v ?? "—");
  return `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const fmtDate = (v) => {
  if (!v) return "—";
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
  return String(v);
};
const dash = (v) => (v == null || v === "" ? "—" : String(v));

const CONF_STYLES = {
  high:   { bg: "bg-[hsl(var(--ploy-pos-soft))]",     text: "text-[hsl(var(--ploy-pos))]",  label: "High" },
  medium: { bg: "bg-[hsl(var(--ploy-attn-soft))]",     text: "text-[hsl(var(--ploy-attn))]", label: "Med" },
  low:    { bg: "bg-[hsl(var(--ploy-rust-soft))]",     text: "text-[hsl(var(--ploy-rust))]", label: "Low" },
};

const DOC_TYPE_LABELS = {
  lease: "Lease", insurance_declaration: "Insurance Declaration", property_tax: "Property Tax",
  pm_statement: "PM Statement", mortgage_statement: "Mortgage Statement",
  mineral_statement: "Mineral / Royalty Statement", other: "Document",
};

// ── Build facts from flat parsed fields when AI-structured facts are absent ──
function buildFallbackFacts(p, docType) {
  const facts = [];
  const add = (field, value, key, format = "text") => {
    if (value == null || value === "") return;
    facts.push({ field, value: String(value), sourceText: "", confidence: "high", key, format });
  };
  // Universal
  add("Loan / Account number", p.loan_number, "loan_number");
  // Mortgage
  if (docType === "mortgage_statement" || p.current_balance != null) {
    add("Outstanding principal balance", p.current_balance, "current_balance", "money");
    add("Monthly payment", p.monthly_payment, "monthly_payment", "money");
    add("Payment due date", p.loan_due_date, "due_date", "date");
  }
  // Lease
  if (docType === "lease") {
    add("Tenant name", p.tenant_name, "tenant_name");
    add("Lease start", p.start_date, "start_date", "date");
    add("Lease end", p.end_date, "end_date", "date");
    add("Monthly rent", p.monthly_rent, "monthly_rent", "money");
    add("Security deposit", p.security_deposit, "security_deposit", "money");
  }
  // Property tax
  if (docType === "property_tax") {
    add("Tax year", p.year, "tax_year");
    add("Tax amount", p.amount, "tax_amount", "money");
  }
  // Insurance
  if (docType === "insurance_declaration") {
    add("Premium amount", p.amount, "amount", "money");
    add("Coverage start", p.start_date, "start_date", "date");
    add("Coverage end", p.end_date, "end_date", "date");
  }
  // PM statement
  if (docType === "pm_statement") {
    add("Monthly payment", p.monthly_payment, "monthly_payment", "money");
    add("Period start", p.start_date, "start_date", "date");
    add("Period end", p.end_date, "end_date", "date");
  }
  // Mineral statement summary facts
  if (docType === "mineral_statement" && p._mineral_summary) {
    const ms = p._mineral_summary;
    const h = ms.header || {};
    add("Paid total", h.paid_total || ms.totals?.paid_total, "paid_total", "money");
    add("Check number", h.check_number, "check_number");
    add("Check date", h.check_date, "check_date", "date");
    add("Wells", ms.well_count, "well_count");
    add("Production lines", ms.line_count, "line_count");
    add("Owner net", ms.totals?.owner_net, "owner_net", "money");
    add("Period", ms.period_min && ms.period_max ? `${ms.period_min} - ${ms.period_max}` : null, "period");
  }
  return facts;
}

function normalizeAIFacts(aiFacts) {
  if (!Array.isArray(aiFacts) || !aiFacts.length) return null;
  return aiFacts.map((f, i) => ({
    field: f.field || `Fact ${i + 1}`,
    value: String(f.value ?? ""),
    sourceText: f.source_text || "",
    confidence: ["high", "medium", "low"].includes(f.confidence) ? f.confidence : "medium",
    key: `ai_fact_${i}`,
    format: "text",
  }));
}

/**
 * DocumentReviewTable — Standardized extraction review for every ingested document.
 *
 * Props:
 *   parsed      — the AI-parsed data bag
 *   docType     — current doc_type string
 *   item        — full ingest item (for metadata)
 *   facts       — controlled fact values [{key, field, value, sourceText, confidence, format}]
 *   onFactsChange — called with updated facts array when user edits a value
 */
export default function DocumentReviewTable({ parsed, docType, item, facts, onFactsChange }) {
  const p = parsed || {};
  const [showSummary, setShowSummary] = useState(false);
  const [editingIdx, setEditingIdx] = useState(null);
  const [editVal, setEditVal] = useState("");

  // Header card metadata
  const header = useMemo(() => ({
    docType: DOC_TYPE_LABELS[docType] || docType || "—",
    issuer: p.payor || p.lender || (p._mineral_summary?.header?.operator) || "—",
    addressedTo: p.addressed_to || "—",
    relatedAsset: p.property_address || p.entity_name || "—",
    owner: "Needs confirmation",
    documentDate: p.document_date || "—",
    periodCovered: (p.period_start && p.period_end)
      ? `${p.period_start} to ${p.period_end}`
      : (p._mineral_summary?.period_min && p._mineral_summary?.period_max)
        ? `${p._mineral_summary.period_min} to ${p._mineral_summary.period_max}`
        : "—",
  }), [p, docType]);

  const startEdit = (idx) => {
    setEditingIdx(idx);
    setEditVal(facts[idx]?.value ?? "");
  };
  const cancelEdit = () => { setEditingIdx(null); setEditVal(""); };
  const saveEdit = (idx) => {
    const next = facts.map((f, i) => i === idx ? { ...f, value: editVal } : f);
    onFactsChange(next);
    setEditingIdx(null);
    setEditVal("");
  };

  const hasLowConfidence = facts.some((f) => f.confidence === "low");

  return (
    <div className="mt-4 space-y-0" data-testid="doc-review-table">
      {/* ── 1. Document Header Card ── */}
      <div
        className="rounded-t-xl border border-[hsl(var(--ploy-border))] bg-[hsl(var(--ploy-panel))] px-5 py-4"
        data-testid="doc-review-header"
      >
        <p className="text-[10px] uppercase tracking-[0.14em] font-bold text-[hsl(var(--ploy-brass-deep))] mb-3">
          Document Summary
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-2.5">
          <HeaderField label="Document type" value={header.docType} testid="dh-doc-type" />
          <HeaderField label="Source / issuer" value={header.issuer} testid="dh-issuer" />
          <HeaderField label="Addressed to" value={header.addressedTo} testid="dh-addressed-to" />
          <HeaderField label="Related asset" value={header.relatedAsset} testid="dh-related-asset" />
          <HeaderField label="Owner" value={header.owner} testid="dh-owner" />
          <HeaderField label="Document date" value={fmtDate(header.documentDate)} testid="dh-doc-date" />
          <HeaderField label="Period covered" value={header.periodCovered} testid="dh-period" />
        </div>
      </div>

      {/* ── 2. Extracted Facts Table ── */}
      <div className="border border-t-0 border-[hsl(var(--ploy-border))] rounded-b-xl overflow-hidden" data-testid="doc-review-facts">
        {hasLowConfidence && (
          <div className="flex items-center gap-2 px-5 py-2.5 bg-[hsl(var(--ploy-rust-soft))] border-b border-[hsl(var(--ploy-border))]">
            <WarningCircle size={14} weight="fill" className="text-[hsl(var(--ploy-rust))] shrink-0" />
            <span className="text-[12px] font-semibold text-[hsl(var(--ploy-rust))]">
              Low-confidence extractions detected — review highlighted rows before accepting.
            </span>
          </div>
        )}
        <table className="w-full text-sm" data-testid="facts-table">
          <thead>
            <tr className="text-[10px] font-bold uppercase tracking-[0.12em] text-[hsl(var(--ploy-brass-deep))] bg-[hsl(var(--ploy-bg-sec))]">
              <th className="text-left px-5 py-2.5 w-[22%]">Field</th>
              <th className="text-left px-3 py-2.5 w-[20%]">Value</th>
              <th className="text-left px-3 py-2.5">Source text</th>
              <th className="text-center px-3 py-2.5 w-[70px]">Confidence</th>
              <th className="text-center px-3 py-2.5 w-[40px]"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[hsl(var(--ploy-border))]">
            {facts.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-5 py-6 text-center text-[hsl(var(--ploy-ink-soft))]">
                  No facts extracted from this document.
                </td>
              </tr>
            ) : facts.map((fact, idx) => {
              const isLow = fact.confidence === "low";
              const isMed = fact.confidence === "medium";
              const isEditing = editingIdx === idx;
              const rowBg = isLow ? "bg-[hsl(var(--ploy-rust-soft))]/40" : isMed ? "bg-[hsl(var(--ploy-attn-soft))]/20" : "";
              return (
                <tr
                  key={fact.key || idx}
                  data-testid={`fact-row-${idx}`}
                  className={`${rowBg} transition-colors`}
                >
                  <td className="px-5 py-2.5 font-semibold text-[hsl(var(--ploy-ink))]">
                    {fact.field}
                  </td>
                  <td className="px-3 py-2.5 tabular-nums text-[hsl(var(--ploy-ink))]">
                    {isEditing ? (
                      <div className="flex items-center gap-1">
                        <input
                          data-testid={`fact-edit-input-${idx}`}
                          autoFocus
                          value={editVal}
                          onChange={(e) => setEditVal(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") saveEdit(idx);
                            if (e.key === "Escape") cancelEdit();
                          }}
                          className="w-full rounded border border-[hsl(var(--ploy-input-border))] bg-[hsl(var(--ploy-input-well))] px-2 py-1 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[hsl(var(--accent))]"
                        />
                        <button onClick={() => saveEdit(idx)} className="text-[hsl(var(--ploy-pos))] hover:opacity-80 p-0.5" data-testid={`fact-save-${idx}`}>
                          <Check size={14} weight="bold" />
                        </button>
                        <button onClick={cancelEdit} className="text-[hsl(var(--ploy-rust))] hover:opacity-80 p-0.5">
                          <X size={14} weight="bold" />
                        </button>
                      </div>
                    ) : (
                      <span className="font-mono">{formatValue(fact.value, fact.format)}</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-[12px] text-[hsl(var(--ploy-ink-soft))] italic max-w-[300px]">
                    <span className="line-clamp-2">{fact.sourceText || "—"}</span>
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    <ConfidenceBadge level={fact.confidence} testid={`fact-conf-${idx}`} />
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    {!isEditing && (
                      <button
                        data-testid={`fact-edit-btn-${idx}`}
                        onClick={() => startEdit(idx)}
                        className="text-[hsl(var(--ploy-ink-soft))] hover:text-[hsl(var(--ploy-ink))] transition-colors p-0.5"
                        title="Edit value"
                      >
                        <PencilSimple size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ── Collapsible original summary ── */}
      {p.summary && (
        <div className="mt-2">
          <button
            data-testid="toggle-original-summary"
            onClick={() => setShowSummary((s) => !s)}
            className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-[hsl(var(--ploy-ink-soft))] hover:text-[hsl(var(--ploy-ink))] transition-colors"
          >
            {showSummary ? <CaretDown size={11} /> : <CaretRight size={11} />}
            {showSummary ? "Hide" : "Show"} original summary
          </button>
          {showSummary && (
            <p className="text-[13px] text-[hsl(var(--ploy-ink-soft))] mt-1.5 pl-4 border-l-2 border-[hsl(var(--ploy-border))] leading-relaxed">
              {p.summary}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ── Sub-components ──
const HeaderField = ({ label, value, testid }) => (
  <div data-testid={testid}>
    <p className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[hsl(var(--ploy-brass-deep))]">{label}</p>
    <p className="text-[13px] font-medium text-[hsl(var(--ploy-ink))] mt-0.5 truncate">{dash(value)}</p>
  </div>
);

const ConfidenceBadge = ({ level, testid }) => {
  const s = CONF_STYLES[level] || CONF_STYLES.medium;
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${s.bg} ${s.text}`}>
      {level === "low" && <WarningCircle size={10} weight="fill" />}
      {level === "high" && <CheckCircle size={10} weight="fill" />}
      {s.label}
    </span>
  );
};

function formatValue(val, format) {
  if (val == null || val === "") return "—";
  if (format === "money") return fmt$(val);
  if (format === "date") return fmtDate(val);
  if (format === "percent") return `${val}%`;
  return String(val);
}

// ── Exported helper: build initial facts array for an ingest item ──
export function buildInitialFacts(parsed, docType) {
  const p = parsed || {};
  // Prefer AI-structured facts when available
  const aiFacts = normalizeAIFacts(p.extracted_facts);
  if (aiFacts) return aiFacts;
  // Fallback: generate from flat parsed fields
  return buildFallbackFacts(p, docType);
}
